import { Boxes, ClipboardList, PackageMinus } from "lucide-react"
import { useEffect } from "react"
import { Navigate, useNavigate, useParams } from "react-router"

import { Pestanas } from "@/componentes/ui/Pestanas"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"
import { Inventario } from "@/paginas/interno/Inventario"
import { Solicitudes } from "@/paginas/interno/suministros/Solicitudes"

/**
 * Las vistas del departamento, cada una con los permisos que la abren.
 *
 * ⚠ **El orden es el del circuito**, no el alfabético: primero lo que alguien pide,
 * después lo que hay para dárselo.
 *
 * ⚠ **«Solicitudes» acepta CUALQUIERA de cuatro llaves.** Quien pide, quien las ve todas,
 * quien decide y quien atiende entran a la misma pantalla y ven cosas distintas dentro.
 * Con una sola llave, a quien sólo aprueba le saldría un departamento sin la pestaña en
 * la que trabaja. Es el mismo «cualquiera de» que el servidor aplica en su lectura.
 */
export const VISTAS = [
  {
    valor: "solicitudes",
    etiqueta: "Solicitudes",
    icono: ClipboardList,
    permisos: [
      "suministro:solicitar",
      "suministro:ver",
      "suministro:aprobar",
      "suministro:atender",
    ],
  },
  {
    valor: "consumibles",
    etiqueta: "Consumibles",
    icono: PackageMinus,
    permisos: ["inventario:ver"],
  },
  {
    valor: "activos",
    etiqueta: "Activos fijos",
    icono: Boxes,
    permisos: ["inventario:ver"],
  },
] as const

type Vista = (typeof VISTAS)[number]["valor"]

const SUBTITULO: Record<Vista, string> = {
  solicitudes:
    "Lo que pide la gente de la casa. Se aprueba, y entonces se entrega del depósito o se compra con una orden de pago.",
  consumibles: "Lo que se agota y hay que reponer: papelería, limpieza, consumibles.",
  activos: "Lo que la agencia posee y no se consume: equipos, mobiliario, herramientas.",
}

/**
 * Cadena de suministros: el departamento, con sus vistas en pestañas.
 *
 * ⚠ **«Inventario» dejó de ser una entrada del menú.** Era una pantalla con dos pestañas
 * propias —consumibles y bienes— y hoy esas dos pestañas son del departamento, al lado de
 * las solicitudes que las mueven. La barra lateral no crece con una línea por cada
 * pantalla de un mismo departamento, y lo que se pide y lo que hay se miran juntos: ésa
 * es la pregunta que este módulo contesta.
 *
 * ⚠ **Dos llaves, a propósito.** `cadena_suministro:ver` ofrece la entrada y nada más;
 * cada pestaña sigue pidiendo la suya. Pedir material lo puede hacer cualquiera de la
 * casa y ver el valor del depósito no, así que con una sola llave habría que elegir entre
 * cerrarle el módulo al tramitador o abrirle el inventario entero.
 *
 * ⚠ **La vista va en la URL** (`/cadena-suministros/consumibles`) y se cambia con
 * `replace`, igual que en Tesorería y Administración: se puede recargar y compartir, y
 * recorrer las pestañas no apila historial. `/inventario`, la dirección vieja, lleva a
 * los consumibles.
 */
export function CadenaSuministros() {
  const usuario = usarSesion((e) => e.usuario)
  const { vista: enRuta } = useParams()
  const navegar = useNavigate()

  const visibles = VISTAS.filter((v) => v.permisos.some((p) => tienePermiso(usuario, p)))
  const vista: Vista | undefined =
    visibles.find((v) => v.valor === enRuta)?.valor ?? visibles[0]?.valor

  useEffect(() => {
    if (vista !== undefined && enRuta !== vista) {
      navegar(`/cadena-suministros/${vista}`, { replace: true })
    }
  }, [enRuta, vista, navegar])

  return (
    <div>
      <header className="mb-4">
        <h1 className="text-xl font-semibold">Cadena de suministros</h1>
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          {vista ? SUBTITULO[vista] : "Lo que hace falta, lo que hay y cómo se consigue."}
        </p>
      </header>

      {vista === undefined ? (
        <p className="tarjeta text-sm" style={{ color: "var(--fg-muted)" }}>
          No tienes acceso a ninguna de sus vistas. Cada una pide su propio permiso, y se conceden
          desde «Roles y permisos».
        </p>
      ) : (
        <>
          <Pestanas
            pestanas={visibles}
            activa={vista}
            onCambiar={(v) => navegar(`/cadena-suministros/${v}`, { replace: true })}
            etiqueta="Vistas de cadena de suministros"
            idPanel="panel-suministros"
            prefijoId="pestana-suministros"
          />
          <div
            id="panel-suministros"
            role="tabpanel"
            aria-labelledby={`pestana-suministros-${vista}`}
            className="mt-4"
          >
            {vista === "solicitudes" && <Solicitudes />}
            {vista === "consumibles" && <Inventario clase="CONSUMIBLE" />}
            {vista === "activos" && <Inventario clase="BIEN" />}
          </div>
        </>
      )}
    </div>
  )
}

/**
 * `/inventario`, la dirección de cuando el depósito era una entrada suelta.
 *
 * Hay marcadores y enlaces guardados, y una redirección es una línea. Lo que no vale es
 * confiar en que el departamento «cae a la primera pestaña»: sin esto, `/inventario`
 * sería un 404 en un sistema donde la pantalla sigue existiendo.
 */
export function RutaInventarioVieja() {
  return <Navigate to="/cadena-suministros/consumibles" replace={true} />
}
